import React from 'react';

/**
 * KineticHero — reusable dynamic hero with animated grid + slow-spin radial glow.
 * Supports per-page palette accents to give contextual cues.
 *
 * Props:
 *   eyebrow   — small uppercase label above the title
 *   title     — main heading text (stays dark)
 *   accent    — optional trailing text rendered in palette gradient
 *   subtitle  — supporting description text
 *   right     — optional node rendered to the right of title block
 *   palette   — 'blue' | 'emerald' | 'amber' | 'violet' | 'rose' | 'dark'
 *   dotColor  — (deprecated, kept for back-compat) explicit pulse-dot color
 *   uid       — unique id to scope animation keyframes
 */

const PALETTES = {
  blue:    { from: '#004ac6', to: '#7c3aed', dot: '#10b981', glow: 'rgba(0,74,198,0.06)', glow2: 'rgba(124,58,237,0.05)', eyebrow: '#434655' },
  emerald: { from: '#059669', to: '#0891b2', dot: '#10b981', glow: 'rgba(5,150,105,0.07)', glow2: 'rgba(8,145,178,0.05)', eyebrow: '#065f46' },
  amber:   { from: '#d97706', to: '#dc2626', dot: '#f59e0b', glow: 'rgba(217,119,6,0.07)',  glow2: 'rgba(220,38,38,0.05)',  eyebrow: '#92400e' },
  violet:  { from: '#7c3aed', to: '#db2777', dot: '#a78bfa', glow: 'rgba(124,58,237,0.08)', glow2: 'rgba(219,39,119,0.05)', eyebrow: '#5b21b6' },
  rose:    { from: '#e11d48', to: '#7c3aed', dot: '#fb7185', glow: 'rgba(225,29,72,0.07)',  glow2: 'rgba(124,58,237,0.05)', eyebrow: '#9f1239' },
  dark:    { from: '#0b1c30', to: '#1e40af', dot: '#60a5fa', glow: 'rgba(11,28,48,0.08)',   glow2: 'rgba(30,64,175,0.05)',  eyebrow: '#0b1c30' },
};

export default function KineticHero({
  eyebrow,
  title,
  accent,
  subtitle,
  right,
  palette = 'blue',
  dotColor, // back-compat override
  uid = 'kh',
}) {
  const p = PALETTES[palette] || PALETTES.blue;
  const pulseDot = dotColor || p.dot;
  const spinKey = `slowSpin_${uid}`;
  const gridClass = `grid-bg-${uid}`;

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
      <style>{`
        @keyframes ${spinKey} { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-${spinKey} { animation: ${spinKey} 40s linear infinite; }
        .${gridClass} { background-image: linear-gradient(${p.glow.replace('0.06','0.05').replace('0.07','0.05').replace('0.08','0.05')} 1px, transparent 1px), linear-gradient(90deg, ${p.glow.replace('0.06','0.05').replace('0.07','0.05').replace('0.08','0.05')} 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>
      <div className={`absolute inset-0 ${gridClass} opacity-60 pointer-events-none`} />
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full animate-${spinKey} pointer-events-none`}
        style={{ background: `radial-gradient(circle, ${p.glow} 0%, ${p.glow2} 40%, transparent 70%)` }}
      />

      <div className="relative px-6 lg:px-8 py-8 lg:py-10 max-w-[1400px] mx-auto flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-4 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: pulseDot }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: p.eyebrow }}>{eyebrow}</span>
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.05] mb-2">
            {title}
            {accent && (
              <>
                {' '}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(90deg, ${p.from}, ${p.to})` }}
                >
                  {accent}
                </span>
              </>
            )}
          </h1>
          {subtitle && <p className="text-sm text-[#434655] max-w-2xl">{subtitle}</p>}
        </div>
        {right && <div className="shrink-0 relative z-10">{right}</div>}
      </div>
    </section>
  );
}
